"use client";

import { useTranslations } from "next-intl";
import { type ReactNode, useMemo } from "react";

// Defensive wrapper around next-intl's `useTranslations` that survives
// the two recurring failure modes the operator runs into in dev:
//
//   1. **Turbopack HMR transient.** Between a layout-file save and the
//      next commit, deeply-nested client components can re-render under
//      an intermediate React tree that doesn't yet include the
//      regenerated `NextIntlClientProvider`. The hook throws
//      "context from NextIntlClientProvider was not found".
//   2. **Upstream RSC failure.** When a deeper Server Component errors,
//      Next 15 retries the surrounding client subtree outside its
//      parent provider boundary. The hook throws the same error from
//      whatever client component happens to render first; the operator
//      sees a misleading red overlay pointing at the wrong file.
//
// `useSafeTranslations` try/catches the hook call and falls back to a
// caller-supplied map of English copy keyed by the same paths the JSON
// catalog uses. Includes naive `{var}` interpolation so ICU-valued
// keys (e.g. `"via {via}"`) still substitute correctly when running on
// the fallback.
//
// Returns a callable `t(key, values)` with attached `.rich(...)` and
// `.has(...)` helpers that mirror next-intl's surface — so any client
// component that uses `t.rich` for inline JSX or `t.has` for catalog
// presence checks can switch to the safe wrapper without code changes
// to the call sites.

export type FallbackMap = Record<string, string>;

type TranslationValues = Record<string, string | number>;
// Mirror use-intl's `RichTranslationValues`: values are either
// `TranslationValue`-ish (string/number for interpolation like `{n}`) OR
// a component-transformer function for `<b>...</b>`-style rich markers.
// Widened 2026-07-02 after entities/new/page.tsx save-bar hit
// `TS2322: Type 'string' is not assignable to type '(chunks: ReactNode)
// => ReactNode'` — the previous narrower signature forced EVERY value
// to be a transformer, which broke every mixed-interpolation-plus-tag
// site. See docs/12-frontend.md §i18n.
type RichValues = Record<string, string | number | ((chunks: ReactNode) => ReactNode)>;

export type SafeT = ((key: string, values?: TranslationValues) => string) & {
  /**
   * Rich variant — like next-intl's `t.rich`. Each `<tagName>...</tagName>`
   * marker in the catalog string is replaced by the matching component
   * from `components`. Fallback path parses the same tag syntax out of
   * the fallback string and applies the component functions to the
   * captured chunks. Nested tags are NOT supported in fallback mode;
   * the common case (`<strong>...</strong>`) works.
   */
  rich: (key: string, components: RichValues) => ReactNode;
  /**
   * Presence check — like next-intl's `t.has`. Falls back to checking
   * whether the key exists in the supplied fallback map.
   */
  has: (key: string) => boolean;
};

export function useSafeTranslations(namespace: string, fallbacks: FallbackMap): SafeT {
  // next-intl's `t` is a callable with `.rich`, `.raw`, `.has` props.
  // We only mirror `rich` and `has`; `raw` is rare in this codebase.
  // biome-ignore lint/suspicious/noExplicitAny: next-intl t shape isn't exported
  let t: any = null;
  try {
    t = useTranslations(namespace);
  } catch {
    t = null;
  }

  // **Memoised on purpose (2026-09-20).** Previously this built three
  // fresh closures and a fresh `Object.assign` on every render, so `t`
  // had a new identity each time. Any caller that listed `t` in a
  // dependency array therefore re-ran that effect on every render —
  // and when the effect fetched, the `setState` it performed triggered
  // the next render, closing a self-sustaining refetch loop throttled
  // only by network latency. Measured on `/watchlists`: 55 requests to
  // `GET /api/watchlists/<id>/triggers` against 16 to `/members` in one
  // log window (`TriggersSection.tsx`, deps `[watchlistId, t]`).
  //
  // Safe to key on `fallbacks` because every one of the ~100 call sites
  // passes a module-level constant, never an inline object literal.
  return useMemo(() => {
    const base = (key: string, values?: TranslationValues): string => {
      if (t) {
        try {
          const value = t(key, values);
          if (value && value !== key) return value;
        } catch {
          // fall through to fallback map
        }
      }
      return interpolate(fallbacks[key] ?? key, values);
    };

    const rich = (key: string, components: RichValues): ReactNode => {
      if (t) {
        try {
          return t.rich(key, components);
        } catch {
          // fall through to fallback rich parser
        }
      }
      return renderRichFallback(fallbacks[key] ?? key, components);
    };

    const has = (key: string): boolean => {
      if (t) {
        try {
          return t.has(key);
        } catch {
          // fall through
        }
      }
      return key in fallbacks;
    };

    // Build the SafeT as a single Object.assign expression so any bundler
    // (Turbopack, swc) sees the function + its `.rich` / `.has` methods
    // attached in one statement — no post-construction mutation that an
    // aggressive optimizer could elide.
    return Object.assign(base, { rich, has }) as SafeT;
  }, [t, fallbacks]);
}

function interpolate(template: string, values?: TranslationValues): string {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    values[k] != null ? String(values[k]) : `{${k}}`,
  );
}

// Parse a single level of `<tag>content</tag>` markers and apply the
// matching component functions. Plain-string segments between markers
// are kept as-is. Returns a ReactNode[] which React accepts directly
// as children. Nested tags fall back to plain string content
// (`<a><b>x</b></a>` would have its outer `<a>` apply but its inner
// `<b>` rendered literally) — acceptable for the fallback path; the
// real next-intl impl is used whenever the provider is present.
function renderRichFallback(template: string, components: RichValues): ReactNode {
  // First substitute plain `{var}` interpolations against any non-function
  // values in `components`. This lets a message like
  // `"<b>{filled}</b> of <b>{total}</b> required"` render correctly on
  // the fallback path when `filled` + `total` are numbers/strings.
  const interpolated = template.replace(/\{(\w+)\}/g, (_, k: string) => {
    const v = components[k];
    return typeof v === "function" ? `{${k}}` : v != null ? String(v) : `{${k}}`;
  });
  const parts: ReactNode[] = [];
  const re = /<(\w+)>([\s\S]*?)<\/\1>/g;
  let last = 0;
  let m: RegExpExecArray | null = re.exec(interpolated);
  while (m !== null) {
    if (m.index > last) parts.push(interpolated.slice(last, m.index));
    // TS treats RegExp capture groups as `string | undefined`; ts-strict
    // needs the guard even though the group is non-optional in the pattern.
    const tag = m[1];
    const inner = m[2] ?? "";
    const fn = tag !== undefined ? components[tag] : undefined;
    parts.push(typeof fn === "function" ? fn(inner) : inner);
    last = m.index + m[0].length;
    m = re.exec(interpolated);
  }
  if (last < interpolated.length) parts.push(interpolated.slice(last));
  return parts;
}
